import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Search, Wrench, X } from 'lucide-react';

const DISMISS_KEY = 'services_howto_dismissed';

// localStorage can throw (private mode / disabled storage) — never let it break the page.
const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch (_) {
    return false;
  }
};

/**
 * "How Services works" onboarding explainer for the directory (Services.js).
 * Teaches the two sides — find a service vs. offer one — and that a single existing
 * account does BOTH (no separate Pro sign-up), since the marketplace is role-agnostic.
 * Dismissible: teach once, then stay out of the way (persisted in localStorage).
 */
const ServicesHowItWorks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(readDismissed);

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch (_) {
      /* dismissal simply won't persist — acceptable */
    }
  };

  return (
    <Card className="relative mb-6 p-4 sm:p-5">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('services.howToDismiss')}
        className="absolute right-2 top-2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X size={16} aria-hidden="true" />
      </button>

      <h2 className="font-heading text-lg font-semibold pr-8">{t('services.howToTitle')}</h2>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
          <div className="rounded-full bg-background p-2 shrink-0">
            <Search size={18} className="text-primary" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-medium">{t('services.howToSeekTitle')}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{t('services.howToSeekBody')}</p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
          <div className="rounded-full bg-background p-2 shrink-0">
            <Wrench size={18} className="text-primary" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-medium">{t('services.howToOfferTitle')}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{t('services.howToOfferBody')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => navigate('/services/listings/create')}
            >
              {t('services.howToOfferCta')}
            </Button>
          </div>
        </div>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">{t('services.howToBothNote')}</p>
    </Card>
  );
};

export default ServicesHowItWorks;
